import React, { useCallback, useMemo } from 'react';
import { getOrganizationVendorSuggestions } from '@components/autocomplete/AutocompleteVendorOrganizations/helpers';
import type { OrganizationFilterOption } from '@components/autocomplete/AutocompleteVendorOrganizations/types';
import env from '@config/env';
import routes from '@config/routes';
import { useOrganization } from '@context/OrganizationContext';
import { useVendor } from '@context/VendorContext';
import { ProfileSelector } from '@goodfynd/dashboard';
import { useAnchorToggle } from '@goodfynd/hooks';
import { Button, Text } from '@goodfynd/ui';
import { useOrganizationApi } from '@hooks/api';
import { useLocalStorageState } from '@hooks/useLocalStorageState';
import useOrganizationAccess from '@hooks/useOrganizationAccess';
import useVendorOrganizationAccess from '@hooks/useVendorOrganizationAccess';
import { Popover } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import {
  getDashboardRoutePath,
  getOrganizationRoutePath,
} from '@utils/routes-util';
import { uniqWith } from 'lodash';
import { useRouter } from 'next/router';

import type { ProfileSwitcherProps } from './types';

export const ProfileSwitcher = ({
  isOrganization,
  varitant = 'popover',
}: ProfileSwitcherProps) => {
  const api = useOrganizationApi();
  const { pathname, push } = useRouter();
  const [open, anchor, { onClick, onClose }] = useAnchorToggle();

  const { hasAccess } = useOrganizationAccess();
  const { hasAccess: hasAccessFromVendor } = useVendorOrganizationAccess();

  const { organization } = useOrganization();
  const { vendor } = useVendor();

  const [recentProfiles, setRecentProfiles] = useLocalStorageState<
    OrganizationFilterOption[]
  >('profile-history', []);

  const profile = useMemo<OrganizationFilterOption>(() => {
    return {
      id: isOrganization ? organization.id : vendor?.id,
      label: isOrganization
        ? organization.uniqueName || organization.name
        : vendor.uniqueName || vendor?.name,
      type: isOrganization ? 'organization' : 'vendor',
      nameId: isOrganization ? organization.nameId : vendor?.nameId,
      value: isOrganization ? organization.id : vendor?.id,
    };
  }, [
    isOrganization,
    organization.id,
    organization.name,
    organization.nameId,
    organization.uniqueName,
    vendor?.id,
    vendor?.name,
    vendor?.nameId,
    vendor.uniqueName,
  ]);

  const handleSelect = useCallback(
    (id?: string, type?: string) => {
      if (!id) {
        return;
      }

      switch (type) {
        case 'lot':
          window.open(`${env.EVENT_APP_HOST}/dashboard/${id}/overview`);
          break;

        case 'organization':
          if (isOrganization) {
            push(pathname.replace('[organizationId]', id));
          } else {
            push(getOrganizationRoutePath(routes.organization.overview, id));
          }
          break;

        case 'vendor':
          if (isOrganization) {
            push(getDashboardRoutePath(routes.dashboard.overview, id));
          } else {
            push(pathname.replace('[vendorId]', id));
          }
          break;

        case undefined:
        default:
          break;
      }
    },
    [isOrganization, pathname, push]
  );

  const renderSelector = useCallback(
    () => (
      <ProfileSelector<OrganizationFilterOption>
        css={{ padding: 0 }}
        defaultOptions={recentProfiles}
        placeholder="Search organizations..."
        selected={profile.id}
        getOptions={async (request) => {
          try {
            const { organizations, vendors } = await api.autocompleteVendors(
              request
            );

            const results = getOrganizationVendorSuggestions(
              organizations,
              vendors,
              hasAccess || hasAccessFromVendor || false
            );

            return results;
          } catch (error) {
            console.error(error);
            return [];
          }
        }}
        onChange={(option) => {
          if (!option) {
            return;
          }

          setRecentProfiles((previous = []) => {
            const newProfiles = uniqWith(
              [option, ...previous],
              (a, b) => a.type === b.type && a.value === b.value
            ).slice(0, 5);
            return newProfiles;
          });
          handleSelect(option.nameId || option.value, option.type);
        }}
      />
    ),
    [
      api,
      handleSelect,
      hasAccess,
      hasAccessFromVendor,
      profile.id,
      recentProfiles,
      setRecentProfiles,
    ]
  );

  if (varitant === 'display') {
    return <Text>{profile.label}</Text>;
  }

  return (
    <>
      <Button
        color="neutral"
        expanded={open}
        fullWidth
        iconRight="chevron_down_sm"
        iconSize={16}
        justify="space-between"
        onClick={open ? onClose : onClick}
        rank="tertiary"
      >
        <Text>{profile.label ?? 'Select a profile'}</Text>
      </Button>

      {varitant === 'popover' && (
        <Popover
          anchorEl={anchor}
          onClose={onClose}
          open={open}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          sx={{
            '.MuiPopover-paper': {
              padding: 6,
              marginTop: 2,
              borderRadius: 3,
              width: 435,
              height: 436,
            },
          }}
        >
          <Text as="heading5">Switch profile</Text>
          {renderSelector()}
        </Popover>
      )}

      {varitant === 'collapse' && (
        <Collapse in={open}>{renderSelector()}</Collapse>
      )}
    </>
  );
};
